import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { LoadingState, EmptyState, FilterChip } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Wrench, MapPin, User, Calendar } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_STYLES = {
  pendiente: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  asignada: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  en_progreso: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  completada: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  cancelada: 'bg-zinc-700/30 text-zinc-500 border-zinc-600/30',
};

const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  asignada: 'Asignada',
  en_progreso: 'En progreso',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

const FILTERS = [
  { id: 'todas', label: 'Todas' },
  { id: 'pendiente', label: 'Pendientes' },
  { id: 'completada', label: 'Completadas' },
];

export default function InstalacionesPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ordenes, setOrdenes] = useState([]);
  const [filter, setFilter] = useState('todas');

  const fetchOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/ordenes-tecnicas`, { withCredentials: true });
      setOrdenes(response.data || []);
    } catch (err) {
      console.error('Error fetching ordenes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrdenes(); }, [fetchOrdenes]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filtered = filter === 'todas' ? ordenes : ordenes.filter((o) => o.estado === filter);

  return (
    <ComercialLayout
      title="Instalaciones"
      subtitle={`${ordenes.filter((o) => o.estado === 'pendiente').length} pendientes · ${ordenes.filter((o) => o.estado === 'completada').length} completadas`}
      onLogout={handleLogout}
    >
      <div className="space-y-4" data-testid="instalaciones-page">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testId={`instalaciones-filter-${f.id}`}>
              {f.label}
            </FilterChip>
          ))}
        </div>

        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Wrench}
            title="Sin instalaciones"
            description="Las órdenes de instalación definitiva aparecerán aquí cuando se completen los checklists de contrato."
          />
        ) : (
          <div className="space-y-3">
            {filtered.map((o) => (
              <button
                key={o.id}
                onClick={() => o.trial_id && navigate(`/comercial/cliente/${o.trial_id}`)}
                className="w-full p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/20 text-left transition-colors"
                data-testid={`instalacion-card-${o.order_number}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs font-mono text-zinc-500">{o.order_number}</span>
                      <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${ESTADO_STYLES[o.estado] || ESTADO_STYLES.pendiente}`}>
                        {ESTADO_LABELS[o.estado] || o.estado}
                      </span>
                    </div>
                    <p className="text-sm text-white font-medium truncate">{o.empresa || o.nombre_contacto}</p>
                    <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1 truncate">
                      <MapPin className="w-3 h-3 flex-shrink-0" />{o.direccion_completa}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 text-xs text-zinc-400 flex-shrink-0">
                    {o.equipo_a_instalar && <p>Equipo: <span className="text-zinc-300">{o.equipo_a_instalar}</span></p>}
                    {o.tecnico_name && (
                      <p className="flex items-center gap-1"><User className="w-3 h-3" />{o.tecnico_name}</p>
                    )}
                    {o.fecha_preferida && (
                      <p className="flex items-center gap-1"><Calendar className="w-3 h-3" />{o.fecha_preferida}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </ComercialLayout>
  );
}
